import { cn } from "@/lib/utils";

/**
 * BrandLoading Components
 *
 * EverReal branded loading indicators:
 * - Spinner: Rotating gold ring
 * - Skeleton: Shimmer effect placeholders
 * - Pulse: Full-page loading indicator
 * - Dots: Animated dot sequence
 */

/**
 * BrandSpinner - Rotating loader
 */
export interface BrandSpinnerProps {
  /**
   * Spinner size
   */
  size?: "sm" | "md" | "lg" | "xl";

  /**
   * Custom class name
   */
  className?: string;

  /**
   * Show with text label
   */
  label?: string;
}

export const BrandSpinner = ({
  size = "md",
  className,
  label,
}: BrandSpinnerProps) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };

  const borderSizes = {
    sm: "border-2",
    md: "border-[3px]",
    lg: "border-4",
    xl: "border-[5px]",
  };

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <div
        className={cn(
          "rounded-full animate-spin",
          "border-slate-200 border-t-brand-primary",
          sizeClasses[size],
          borderSizes[size],
        )}
        role="status"
        aria-label="Loading"
      />
      {label && <span className="text-sm text-text-secondary">{label}</span>}
    </div>
  );
};

BrandSpinner.displayName = "BrandSpinner";

/**
 * BrandSkeleton - Shimmer placeholder
 */
export interface BrandSkeletonProps {
  /**
   * Skeleton variant
   */
  variant?: "text" | "card" | "circle" | "rectangle" | "button";

  /**
   * Width (for rectangle/custom)
   */
  width?: string | number;

  /**
   * Height (for rectangle/custom)
   */
  height?: string | number;

  /**
   * Number of lines (for text variant)
   */
  lines?: number;

  /**
   * Custom class name
   */
  className?: string;
}

export const BrandSkeleton = ({
  variant = "rectangle",
  width,
  height,
  lines = 1,
  className,
}: BrandSkeletonProps) => {
  const variantClasses = {
    text: "h-4 rounded",
    card: "h-48 rounded-brand",
    circle: "rounded-full aspect-square",
    rectangle: "rounded-md",
    button: "h-10 rounded-button",
  };

  // Base skeleton element
  const SkeletonElement = ({
    className: elementClassName,
  }: {
    className?: string;
  }) => (
    <div
      className={cn(
        "bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100",
        "animate-shimmer bg-[length:200%_100%]",
        elementClassName,
      )}
      role="status"
      aria-label="Loading"
    />
  );

  // Text variant with multiple lines
  if (variant === "text" && lines > 1) {
    return (
      <div className={cn("space-y-2", className)}>
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonElement
            key={i}
            className={cn(
              variantClasses.text,
              // Last line is shorter
              i === lines - 1 && "w-3/4",
            )}
          />
        ))}
      </div>
    );
  }

  // Single skeleton element
  return (
    <SkeletonElement
      className={cn(
        variantClasses[variant],
        width && (typeof width === "number" ? `w-[${width}px]` : width),
        height && (typeof height === "number" ? `h-[${height}px]` : height),
        className,
      )}
    />
  );
};

BrandSkeleton.displayName = "BrandSkeleton";

/**
 * BrandPulse - Full page loading
 */
export interface BrandPulseProps {
  /**
   * Show overlay background
   */
  overlay?: boolean;

  /**
   * Loading message
   */
  message?: string;

  /**
   * Custom class name
   */
  className?: string;
}

export const BrandPulse = ({
  overlay = true,
  message = "Loading...",
  className,
}: BrandPulseProps) => {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-6",
        overlay && "fixed inset-0 z-50 bg-white/90 backdrop-blur-sm",
        className,
      )}
    >
      {/* Pulse rings */}
      <div className="relative w-24 h-24">
        {/* Outer ring */}
        <div className="absolute inset-0 rounded-full border-4 border-brand-primary/20 animate-glow-pulse" />

        {/* Middle ring */}
        <div
          className="absolute inset-3 rounded-full border-4 border-brand-primary/40 animate-glow-pulse"
          style={{ animationDelay: "0.2s" }}
        />

        {/* Inner ring */}
        <div
          className="absolute inset-6 rounded-full border-4 border-brand-primary/60 animate-glow-pulse"
          style={{ animationDelay: "0.4s" }}
        />

        {/* Center glow */}
        <div className="absolute inset-0 rounded-full bg-brand-primary/10 blur-xl animate-glow-pulse" />
      </div>

      {/* Loading message */}
      {message && (
        <p className="text-text-primary font-medium tracking-wide">{message}</p>
      )}
    </div>
  );
};

BrandPulse.displayName = "BrandPulse";

/**
 * BrandDots - Animated dots loader
 */
export interface BrandDotsProps {
  /**
   * Dot size
   */
  size?: "sm" | "md" | "lg";

  /**
   * Custom class name
   */
  className?: string;
}

export const BrandDots = ({ size = "md", className }: BrandDotsProps) => {
  const sizeClasses = {
    sm: "w-1.5 h-1.5",
    md: "w-2 h-2",
    lg: "w-3 h-3",
  };

  const gapClasses = {
    sm: "gap-1",
    md: "gap-1.5",
    lg: "gap-2",
  };

  return (
    <div className={cn("flex items-center", gapClasses[size], className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            "rounded-full bg-brand-primary",
            "animate-glow-pulse",
            sizeClasses[size],
          )}
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
};

BrandDots.displayName = "BrandDots";

/**
 * BrandProgress - Linear progress bar
 */
export interface BrandProgressProps {
  /**
   * Progress value (0-100)
   */
  value?: number;

  /**
   * Show percentage label
   */
  showLabel?: boolean;

  /**
   * Indeterminate loading
   */
  indeterminate?: boolean;

  /**
   * Custom class name
   */
  className?: string;

  /**
   * Bar height
   */
  height?: "sm" | "md" | "lg";
}

export const BrandProgress = ({
  value = 0,
  showLabel = false,
  indeterminate = false,
  className,
  height = "md",
}: BrandProgressProps) => {
  const heightClasses = {
    sm: "h-1",
    md: "h-2",
    lg: "h-3",
  };

  return (
    <div className={cn("w-full space-y-2", className)}>
      {showLabel && !indeterminate && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-text-secondary">Progress</span>
          <span className="text-sm font-semibold text-text-primary">
            {Math.round(value)}%
          </span>
        </div>
      )}

      <div
        className={cn(
          "w-full bg-slate-100 border border-border-card rounded-full overflow-hidden",
          heightClasses[height],
        )}
      >
        {indeterminate ? (
          <div className="h-full bg-gradient-to-r from-brand-primary to-brand-primary-light animate-shimmer bg-[length:200%_100%]" />
        ) : (
          <div
            className="h-full bg-gradient-to-r from-brand-primary to-brand-primary-light transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
          />
        )}
      </div>
    </div>
  );
};

BrandProgress.displayName = "BrandProgress";

/**
 * Skeleton Layouts - Pre-composed skeleton layouts
 */

export const SkeletonCard = ({ className }: { className?: string }) => (
  <div className={cn("p-6 space-y-4", className)}>
    <BrandSkeleton variant="text" width="60%" />
    <BrandSkeleton variant="text" lines={3} />
    <div className="flex gap-2">
      <BrandSkeleton variant="button" width="100px" />
      <BrandSkeleton variant="button" width="100px" />
    </div>
  </div>
);

export const SkeletonTable = ({
  rows = 5,
  className,
}: {
  rows?: number;
  className?: string;
}) => (
  <div className={cn("space-y-3", className)}>
    {/* Header */}
    <div className="grid grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <BrandSkeleton key={i} variant="text" />
      ))}
    </div>

    {/* Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, j) => (
          <BrandSkeleton key={j} variant="text" />
        ))}
      </div>
    ))}
  </div>
);

export const SkeletonList = ({
  items = 3,
  className,
}: {
  items?: number;
  className?: string;
}) => (
  <div className={cn("space-y-4", className)}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center gap-4">
        <BrandSkeleton variant="circle" width="48px" height="48px" />
        <div className="flex-1 space-y-2">
          <BrandSkeleton variant="text" width="40%" />
          <BrandSkeleton variant="text" width="80%" />
        </div>
      </div>
    ))}
  </div>
);
